// frontend/src/context/FavoritesContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext();

export const useFavorites = () => {
    return useContext(FavoritesContext);
};

export const FavoritesProvider = ({ children }) => {
    const { token } = useAuth();
    const [favorites, setFavorites] = useState([]);
    const [favoriteIds, setFavoriteIds] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [authChecked, setAuthChecked] = useState(false);

    const fetchFavorites = useCallback(async () => {
        if (!token) {
            setLoading(false);
            setAuthChecked(true);
            setFavorites([]);
            setFavoriteIds(new Set());
            return;
        }

        setLoading(true);
        try {
            const response = await api.get('/favoritos/me');
            const favoritosList = response.data || []; // Lista de objetos Favorito

            // --- ¡MODIFICACIÓN 1 (LA CLAVE)! ---
            // Extraemos el objeto 'item' de cada 'favorito'
            const items = favoritosList.map(favorito => favorito.item);

            // Almacenamos la lista de Items
            setFavorites(items);

            // Creamos el Set de IDs usando item.id (el ID del producto)
            setFavoriteIds(new Set(items.map(item => item.id)));
            // ---------------------------------

        } catch (error) {
            console.error("Error al cargar favoritos (quizás el token expiró):", error);
            setFavorites([]);
            setFavoriteIds(new Set());
        } finally {
            setLoading(false);
            setAuthChecked(true);
        }
    }, [token]);

    // Recargar favoritos cuando cambia el token (login/logout)
    useEffect(() => {
        fetchFavorites();
    }, [fetchFavorites]);

    // Función para AÑADIR un favorito
    const addFavorite = async (item) => {
        if (favoriteIds.has(item.id)) return;
        try {
            // Actualización optimista (añade el objeto Item)
            setFavorites(prev => [...prev, item]);
            setFavoriteIds(prev => new Set(prev).add(item.id));

            await api.post(`/favoritos/`, { item_id: item.id });
        } catch (error) {
            console.error("Error al añadir favorito:", error);
            // Revertir si falla
            setFavorites(prev => prev.filter(fav => fav.id !== item.id));
            setFavoriteIds(prev => {
                const newIds = new Set(prev);
                newIds.delete(item.id);
                return newIds;
            });
        }
    };

    // Función para QUITAR un favorito
    const removeFavorite = async (itemId) => {
        if (!favoriteIds.has(itemId)) return;
        try {
            // Actualización optimista
            setFavorites(prev => prev.filter(fav => fav.id !== itemId));
            setFavoriteIds(prev => {
                const newIds = new Set(prev);
                newIds.delete(itemId);
                return newIds;
            });

            // --- ¡MODIFICACIÓN 2! ---
            // Quitamos la barra (/) al final. FastAPI la quiere sin barra
            // cuando se usa un path parameter como {item_id}.
            await api.delete(`/favoritos/${itemId}`);

        } catch (error) {
            console.error("Error al quitar favorito:", error);
            fetchFavorites(); // Recargar en caso de error
        }
    };

    // Valor a exponer
    const value = {
        favorites,
        favoriteIds,
        addFavorite,
        removeFavorite,
        fetchFavorites,
        loadingFavorites: loading,
        authChecked
    };

    return (
        <FavoritesContext.Provider value={value}>
            {children}
        </FavoritesContext.Provider>
    );
};